import { Children } from 'react';
import type { ReactNode } from 'react';
import { SparkleIcon } from '@phosphor-icons/react';
import { useGenUiConfig } from './config.js';

/**
 * Rodapé compartilhado dos cards de reserva (Proposta B): a nota do agente com
 * ícone sparkle + as ações. Renderiza nada quando não há nota nem ações (degrada
 * limpo). É o único pedaço de chrome comum aos quatro cards — por isso vive em
 * `shared/`, igual aos formatadores e ao CSS.
 *
 * O `note` é texto do agente (pode trazer markdown, ex.: `**negrito**`); renderiza
 * via `renderMarkdown` injetado pelo shell (`GenUiConfig`) — mesmo renderer/
 * segurança das bolhas de mensagem. Sem o renderer (preview/testes) cai no texto
 * puro. Mesmo padrão do `rental_offers`.
 */
export function CardFooter({
  note,
  className,
  children,
}: {
  readonly note?: string;
  /** Utilitárias do DS somadas à casca (`fui-foot`) por quem pede outro ritmo. */
  readonly className?: string;
  readonly children?: ReactNode;
}) {
  const { renderMarkdown } = useGenUiConfig();
  // `Children.toArray` e não `!children`: o truthy de `children` mede a FORMA do
  // JSX do caller, não o que ele renderiza. Com UM filho condicional o React
  // entrega `false` cru e `!children` acertava; com DOIS ele entrega o array
  // `[false, false]`, que é truthy — e a banda saía vazia (muted + borda no pé de
  // um card sem ação nenhuma). O espelho do mesmo erro engolia conteúdo real:
  // `{0}` é falsy e derrubava o rodapé inteiro. `toArray` descarta booleano,
  // `null` e `undefined`, achata os aninhados e PRESERVA o `0` — é a pergunta
  // certa, "sobrou algo pra renderizar?", em vez de "o filho é truthy?".
  if (!note && Children.toArray(children).length === 0) return null;
  return (
    <div className={className ? `fui-foot ${className}` : 'fui-foot'}>
      {note && (
        <div className="fui-note">
          <SparkleIcon size={15} weight="fill" aria-hidden />
          {/* O markdown rende vários blocos irmãos (p/ul/strong); embrulha num
              único container pra ocupar UMA célula do grid `auto 1fr` — sem isso
              os blocos espalham pelas duas colunas e a nota quebra. */}
          <div className="fui-note-body">
            {renderMarkdown ? renderMarkdown(note) : <p>{note}</p>}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}
